/**
 * Sidebar Component
 * Navigation sidebar/drawer with menu items.
 * Supports a `collapsed` mode that shows only icons (no labels) and exposes
 * an inline toggle button to expand/collapse the drawer.
 */

import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Badge,
  Box,
  Tooltip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Computer as AgentsIcon,
  QueuePlayNext as EnrollmentsIcon,
  SystemUpdate as UpdatesIcon,
  Settings as SettingsIcon,
  ChatBubble as ChatIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import type { NavItem } from '@/types/navigation';
import { useChatStore } from '@/store/chatStore';
import { useEnrollmentsStore } from '@/store/enrollmentsStore';
import { useRailFollow } from './useRailFollow';

const DRAWER_WIDTH = 240;
export const DRAWER_COLLAPSED_WIDTH = 64;

interface SidebarProps {
  open: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'temporary';
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    id: 'agents',
    label: 'Agentes',
    path: '/agents',
    icon: <AgentsIcon />,
  },
  {
    id: 'enrollments',
    label: 'Enrollments',
    path: '/enrollments',
    icon: <EnrollmentsIcon />,
    // badge inyectado dinámicamente desde el store
  },
  {
    id: 'updates',
    label: 'Actualizaciones',
    path: '/updates',
    icon: <UpdatesIcon />,
  },
  {
    id: 'chat',
    label: 'Chat',
    path: '/chat',
    icon: <ChatIcon />,
    // badge inyectado dinámicamente desde el store
  },
];

const settingsItems: NavItem[] = [
  {
    id: 'settings',
    label: 'Configuración',
    path: '/config',
    icon: <SettingsIcon />,
  },
];

/**
 * CollapseRail
 * Invisible vertical strip on the right edge of the sidebar that drives
 * a floating collapse/expand handle. The rail itself has no visual
 * presence — only a circular handle renders, protruding past the drawer's
 * right edge with an elevated shadow to read as a floating control.
 *
 * All hover/follow-mouse logic lives in `useRailFollow` so it can be
 * unit-tested in isolation; this component just wires the hook to the
 * portal'd handle.
 */
interface CollapseRailProps {
  collapsed: boolean;
  onToggle: () => void;
}

const COLLAPSE_BUTTON_SIZE = 24;
const COLLAPSE_RAIL_WIDTH = 28;
const COLLAPSE_RAIL_PADDING = 8; // min space between handle and rail top/bottom

function CollapseRail({ collapsed, onToggle }: CollapseRailProps) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const { buttonTop, buttonLeft, mounted, isPositioned } = useRailFollow(railRef, {
    buttonSize: COLLAPSE_BUTTON_SIZE,
    railPadding: COLLAPSE_RAIL_PADDING,
  });

  const label = collapsed ? 'Expandir menú' : 'Colapsar menú';

  const handleButton = (
    <Tooltip title={label} placement="left" arrow>
      <IconButton
        aria-label={label}
        onClick={onToggle}
        size="small"
        data-testid="sidebar-collapse-handle"
        sx={{
          // position: fixed escapes every ancestor's stacking/overflow
          // context, so the button is guaranteed to render fully outside
          // the drawer even when overflow clipping would otherwise hide it.
          position: 'fixed',
          top: buttonTop,
          left: buttonLeft,
          width: COLLAPSE_BUTTON_SIZE,
          height: COLLAPSE_BUTTON_SIZE,
          borderRadius: '50%',
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          color: 'text.secondary',
          // Elevated floating shadow (subtle at rest, deeper on hover)
          boxShadow: '0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)',
          zIndex: 1200,
          // `top` eases so the button *approaches* the mouse smoothly
          // (instead of snapping) and also eases back when the mouse
          // leaves the rail.
          //
          // `left` has NO transition on purpose: the handle is
          // `position: fixed` and its `left` is derived from the rail's
          // right edge, which the drawer's width animation is moving
          // continuously. If we eased `left` too, the button would
          // chase the moving right edge with its own curve and visibly
          // lag behind the drawer during collapse/expand. By snapping
          // `left` to the latest measured value, the handle tracks the
          // drawer's edge 1:1 — ResizeObserver fires every frame the
          // width is animating, so the handle follows the menu's motion
          // exactly (reads as "part of the same menu").
          transition:
            'top 0.22s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.2s',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'primary.main',
            color: 'primary.main',
            boxShadow: '0 4px 12px rgba(0,0,0,0.22), 0 2px 4px rgba(0,0,0,0.10)',
          },
        }}
      >
        {collapsed ? (
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        ) : (
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {/* Invisible hover area — a thin absolute strip on the right edge of
          the drawer. It is a pure *position marker* for `useRailFollow`,
          which reads this box's bounding rect to define the extended
          hover area. Hover tracking itself happens on `window` (see
          useRailFollow), so the rail does NOT need to receive pointer
          events.

          `pointerEvents: 'none'` is mandatory: when the drawer is
          collapsed, the nav icons sit centered in the 64px-wide drawer
          and the rail (28px wide, at the right edge) would otherwise
          overlap the rightmost ~8px of every icon. The rail is rendered
          after the drawer content, so without `pointerEvents: 'none'`
          it would swallow clicks on the right side of every nav item.
          Disabling pointer events lets clicks fall through to whatever
          is underneath (nav items) while keeping the rail's
          getBoundingClientRect measurements intact for hover tracking.

          No `cursor: 'pointer'` here on purpose: the rail is not
          interactive, so showing a pointer cursor over it would be
          misleading. The actual clickable surface is the floating
          button itself. */}
      <Box
        ref={railRef}
        data-testid="sidebar-collapse-rail"
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: COLLAPSE_RAIL_WIDTH,
          pointerEvents: 'none',
        }}
      />
      {/* Floating button rendered to <body> via portal — escapes every
          stacking/overflow constraint of the drawer tree. Only renders
          once we have valid bounds, so it never flashes at 0:0. */}
      {mounted && isPositioned && createPortal(handleButton, document.body)}
    </>
  );
}

// Exported for tests so the rail's hover-tracking behavior can be covered
// in isolation without standing up the full Sidebar (Drawer + Theme + stores).
export { CollapseRail };

export default function Sidebar({
  open,
  onClose,
  variant = 'permanent',
  collapsed = false,
  onToggleCollapsed,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chatUnread = useChatStore((s) => s.totalUnread);
  const enrollmentsCount = useEnrollmentsStore((s) => s.enrollments.length);

  const currentWidth = collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;

  // On mobile, `collapsed` is meaningless: the drawer is a temporary
  // overlay with a fixed width and labels must always show. We force
  // `false` here so every downstream check (width, labels, tooltips,
  // centered layout) treats mobile as if collapsed never existed.
  const effectiveCollapsed = isMobile ? false : collapsed;
  const showLabels = !effectiveCollapsed;

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile || variant === 'temporary') {
      onClose?.();
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);

    const buttonSx = {
      borderRadius: 1,
      mx: 1,
      my: 0.25,
      // Centrar contenido cuando está colapsado
      justifyContent: showLabels ? 'flex-start' : 'center',
      px: showLabels ? 2 : 1,
      '&.Mui-selected': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
        '& .MuiListItemIcon-root': {
          color: 'primary.contrastText',
        },
      },
    };

    const button = (
      <ListItemButton
        selected={active}
        onClick={() => handleNavigate(item.path)}
        disabled={item.disabled}
        sx={buttonSx}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            // Compensar el padding interno cuando está colapsado
            mr: showLabels ? 2 : 0,
            color: active ? 'primary.contrastText' : 'inherit',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {showLabels && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 400,
            }}
          />
        )}
        {showLabels &&
          item.badge !== undefined &&
          typeof item.badge === 'number' &&
          item.badge > 0 && (
            <Badge
              badgeContent={item.badge}
              color={item.id === 'enrollments' ? 'warning' : 'error'}
              sx={{
                '& .MuiBadge-badge': {
                  right: 8,
                },
              }}
            />
          )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.id} disablePadding>
        {!showLabels ? (
          <Tooltip title={item.label} placement="right" arrow>
            {/* span necesario porque Tooltip necesita un elemento hijo que acepte ref */}
            <Box sx={{ width: '100%', display: 'flex' }}>{button}</Box>
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  // Inject dynamic badges (chat unread, enrollments pending).
  const dynamicNavItems = navItems.map((item) => {
    if (item.id === 'chat') return { ...item, badge: chatUnread };
    if (item.id === 'enrollments') return { ...item, badge: enrollmentsCount };
    return item;
  });

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Spacer for the AppBar. Required on both desktop (permanent drawer)
          and mobile (temporary overlay) — the AppBar's zIndex
          (theme.zIndex.drawer + 1) sits above the drawer's paper
          (theme.zIndex.drawer), so without this spacer the first nav item
          (Dashboard) is hidden BEHIND the AppBar on mobile even though the
          drawer itself reports the full viewport height. The previous
          comment claimed the mobile drawer covered the AppBar — it does
          not, in stacking order it is below it. */}
      <Toolbar />
      <Box sx={{ overflow: 'auto', py: 1, flexGrow: 1 }}>
        <List>{dynamicNavItems.map(renderNavItem)}</List>
      </Box>
      {/* Sticky footer: Configuración */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <List sx={{ py: 0 }}>{settingsItems.map(renderNavItem)}</List>
      </Box>
    </Box>
  );

  // The collapse rail only belongs on the permanent (desktop) drawer on
  // sm+ screens. On mobile the Header already exposes a hamburger button
  // that toggles the temporary drawer, so we skip the rail entirely —
  // otherwise its portal would still render the floating button on top of
  // the mobile UI even with the permanent drawer at `display: none`.
  const showCollapseRail = !isMobile && variant === 'permanent' && !!onToggleCollapsed;

  return (
    <>
      {/* Desktop drawer (permanent, can be collapsed) */}
      <Drawer
        variant={variant}
        open={open}
        onClose={onClose}
        sx={{
          width: currentWidth,
          flexShrink: 0,
          display: { xs: 'none', sm: variant === 'permanent' ? 'block' : 'none' },
          '& .MuiDrawer-paper': {
            width: currentWidth,
            boxSizing: 'border-box',
            outline: 'none',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            // Allow horizontal overflow so the floating collapse handle can
            // protrude past the drawer's right edge. The visible nav content
            // is still constrained to `currentWidth` via its own layout.
            overflowX: 'visible',
          },
        }}
      >
        {drawerContent}
        {/* Edge rail with floating collapse handle. Rendered as a sibling
            of `drawerContent` (still inside the paper) so its absolute
            positioning is relative to the drawer's box. */}
        {showCollapseRail && onToggleCollapsed && (
          <CollapseRail collapsed={collapsed} onToggle={onToggleCollapsed} />
        )}
      </Drawer>

      {/* Mobile drawer (temporary overlay, always full-width) */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            outline: 'none',
            borderRadius: 0,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
